import { faLineColumns, faSwap } from '@awesome.me/kit-2f991a40ac/icons/classic/solid';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { LpRidLeeslijstConfig, LpRidMechanicConfig, LpRidWisselklankenConfig, LpTopicFilters } from '../../typings/schema';

/** The categories mechanics are grouped in, each with the color it's shown in. */
export const RID_MECHANIC_CATEGORIES = {
  spellen: { color: 'var(--color-orange-500)' },
  taalinzicht: { color: 'var(--color-cyan-500)' },
  lezen: { color: 'var(--color-purple-500)' },
  games: { color: 'var(--color-lime-500)' },
  misc: { color: 'var(--color-gray-500)' },
} as const;

export type RidMechanicCategory = keyof typeof RID_MECHANIC_CATEGORIES;

export interface RidMechanic<TConfig extends LpRidMechanicConfig> {
  name: string;
  icon: IconDefinition;
  category: RidMechanicCategory;
  /** Short facts about an assignment, such as its length. */
  details(config: TConfig): string[];
  /** The topic filters the assignment draws its words from; undefined for mechanics that have none. */
  topicFilters(config: TConfig): LpTopicFilters | undefined;
}

/** The mechanics coupled to homework so far, so the only ones an assignment in the game can be for. */
export type RidCoupledMechanicConfig = LpRidWisselklankenConfig | LpRidLeeslijstConfig;

/** Every mechanic coupled to homework, by the dataType of its config. */
export const RID_MECHANICS: {
  [D in RidCoupledMechanicConfig['dataType']]: RidMechanic<Extract<RidCoupledMechanicConfig, { dataType: D }>>;
} = {
  RidWisselklankenConfig: {
    name: 'Wisselklanken',
    icon: faSwap,
    category: 'taalinzicht',
    details: (config) => [`${config.count} ${config.count === 1 ? 'woord' : 'woorden'}`],
    topicFilters: (config) => config.topicFilters,
  },
  RidLeeslijstConfig: {
    name: 'Leeslijst',
    icon: faLineColumns,
    category: 'lezen',
    details: (config) => {
      const mode = config.mode;
      const pages = `${mode.pages} ${mode.pages === 1 ? 'pagina' : "pagina's"}`;
      switch (mode.dataType) {
        case 'RidLeeslijstConfigModeGeenTijd':
          return [pages];
        case 'RidLeeslijstConfigModeStopwatch':
          return ['Stopwatch', pages];
        case 'RidLeeslijstConfigModeTimer':
          return [formatDuration(mode.duration), pages];
      }
    },
    topicFilters: (config) => config.mode.topicFilters,
  },
};

/** The mechanic the config is for. */
export function getRidMechanic(config: LpRidMechanicConfig): RidMechanic<LpRidMechanicConfig> {
  if (!(config.dataType in RID_MECHANICS)) throw new Error(`${config.dataType} isn't coupled to homework yet`);

  return RID_MECHANICS[config.dataType as RidCoupledMechanicConfig['dataType']];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;

  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} min ${rest} sec` : `${minutes} min`;
}
